import { MenuItem } from "@blueprintjs/core"
import { ItemRendererProps, Suggest } from "@blueprintjs/select"
import React, { useCallback, useState } from "react"
import { useSearchDatasets } from "../../hooks/useSearchDatasets"
import { TradingDatasetLight } from "../../types/trading"
import { useDebounce } from "../../views/hooks/useDebounce"

interface DatasetSuggestV2Props {
  onSelect: (dataset: TradingDatasetLight | null) => void
  placeholder?: string
}

export const DatasetSuggestV2: React.FC<DatasetSuggestV2Props> = ({
  onSelect,
  placeholder = "Search datasets...",
}) => {
  const [selectedDataset, setSelectedDataset] = useState<TradingDatasetLight | null>(null)
  const [searchTerm, setSearchTerm] = useState("")
  const { data: datasets = [], isLoading } = useSearchDatasets(searchTerm)

  const debouncedSetSearchTerm = useDebounce((newSearchTerm: string) => {
    setSearchTerm(newSearchTerm)
  }, 500)

  const renderDataset = useCallback(
    (dataset: TradingDatasetLight, { handleClick, modifiers }: ItemRendererProps) => (
      <MenuItem
        active={modifiers.active}
        key={dataset.id}
        onClick={handleClick}
        text={dataset.long_name ? dataset.long_name : dataset.name}
      />
    ),
    [],
  )

  const handleSelect = useCallback(
    (dataset: TradingDatasetLight) => {
      setSelectedDataset(dataset)
      onSelect(dataset)
    },
    [onSelect, setSelectedDataset],
  )

  return (
    <Suggest<TradingDatasetLight>
      items={datasets}
      selectedItem={selectedDataset}
      itemRenderer={renderDataset}
      onItemSelect={handleSelect}
      onQueryChange={debouncedSetSearchTerm}
      inputProps={{ placeholder, large: true }}
      resetOnSelect={true}
      inputValueRenderer={(dataset: TradingDatasetLight) => dataset.long_name || dataset.name}
      noResults={
        <MenuItem
          disabled
          text={
            searchTerm.length < 3
              ? "Type at least 3 characters..."
              : isLoading
                ? "Loading..."
                : "No datasets found"
          }
        />
      }
      fill
    />
  )
}
